import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormProcessing from './form-processing';
import Button from './button';
import * as Styles from './sign-up-requirements-sso-user.module.scss';
import AvatarSelect from './avatar-select';
import { avatarList } from './avatar';
import BirthdayField from './birthday-field';
import InUnitedStatesField from './in-united-states-field';
import RoleField from './role-field';
import EmailField from './email-field';
import ClassCodeField from './class-code-field';
import NewsletterField from './newsletter-field';
import StateField from './state-field';
import SchoolField from './school-field';
import FormError from './form-error';
import { isCoppaAge, isAdultAge, ROLE_TEACHER, ROLE_STUDENT, canUserJoinClass } from '../utils/user';
import { getClassCode, removeClassCode } from '../utils/class-code';
import { useAuth } from '../contexts/AuthContext';
import { useSite } from '../contexts/SiteContext';
import GradeBandField from './grade-band-field';

function SignUpRequirementsSsoUser({ scrollToTop }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const {
    user,
    profile,
    setEmail: authSetEmail,
    isGoogleUser,
    setOptIns,
    signUpAdditionalData,
    getSchoolFromId,
    validateEmail,
    signOut,
  } = useAuth();
  const { joinClassroom } = useSite();

  /**
   * If user has previous set role in profile
   *
   * @returns boolean
   */
  function hasRole() {
    return profile && profile.roles && profile.roles.length > 0;
  }

  const [avatar, setAvatar] = useState(avatarList[0]);
  const [birthday, setBirthday] = useState('');
  const [age, setAge] = useState(0);
  const [inUnitedStates, setInUnitedStates] = useState('');
  const [roles, setRoles] = useState(hasRole() ? profile.roles : []);
  const [email, setEmail] = useState(user && user.email ? user.email : '');
  const [gradeBand, setGradeBand] = useState('');
  const [code, setCode] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [state, setState] = useState('');
  const [schoolId, setSchoolId] = useState('');

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (hasRole()) {
      setRoles(profile.roles);
    } else {
      setRoles(isAdultAge(age) ? [ROLE_TEACHER] : [ROLE_STUDENT]);
    }
  }, [age, profile.roles]);

  useEffect(() => {
    if (inUnitedStates === 'yes') {
      setNewsletter(true);
    } else if (inUnitedStates === 'no') {
      setNewsletter(false);
    }
  }, [inUnitedStates]);

  function canRole() {
    return !!age && inUnitedStates && !hasRole() && isAdultAge(age);
  }

  function canEmail() {
    return !!age && isAdultAge(age) && inUnitedStates && !roles.includes(ROLE_STUDENT);
  }

  function canSchool() {
    return !!age && isCoppaAge(age) && inUnitedStates === 'yes';
  }

  function canClassCode() {
    return (
      canUserJoinClass({ roles }) && !!age && ((inUnitedStates && isCoppaAge(age)) || !isCoppaAge(age)) && isGoogleUser
    );
  }

  function canOptIn() {
    return isAdultAge(age) && inUnitedStates && !roles.includes(ROLE_STUDENT);
  }

  function canGradeBand() {
    return !!age && isCoppaAge(age) && inUnitedStates === 'yes';
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (avatar === avatarList[0]) {
      setError('Please select an avatar');
      scrollToTop();
      return;
    }

    if (!email && canEmail()) {
      setError('Please enter your email.');
      scrollToTop();
      return;
    }

    if (email && canEmail()) {
      try {
        await validateEmail(email);
      } catch (err) {
        setError(err.message);
        scrollToTop();
        return;
      }
    }

    if (!state && canSchool()) {
      setError('Please select a state.');
      scrollToTop();
      return;
    }

    if (!schoolId && canSchool()) {
      setError('Please select a school.');
      scrollToTop();
      return;
    }

    if (!gradeBand && canGradeBand()) {
      setError('Please enter your grade.');
      scrollToTop();
      return;
    }

    setIsProcessing(true);

    const data = {
      avatar,
      completedProfile: true,
      adultAge: isAdultAge(age),
      coppaAge: isCoppaAge(age),
    };

    if (isCoppaAge(age)) {
      data.birthday = new Date(birthday);
      data.inUnitedStates = inUnitedStates === 'yes';
    }

    if (canRole()) {
      data.roles = roles;
    } else if (!hasRole() && !isAdultAge(age)) {
      data.roles = [ROLE_STUDENT];
    }

    if (canSchool()) {
      data.state = state;

      try {
        data.schoolData = await getSchoolFromId(schoolId.value);
      } catch (err) {
        // error silently
      }
    }

    if (canGradeBand()) {
      data.gradeBand = gradeBand;
    }

    try {
      await signUpAdditionalData(data);
    } catch (err) {
      setError(err.message);
      setIsProcessing(false);
      scrollToTop();
      return;
    }

    if (code && canClassCode()) {
      joinClassroom(code).catch(() => {});
    }
    removeClassCode();

    if (canEmail()) {
      authSetEmail(email, !isAdultAge(age)).catch(() => {});
    }

    if (canOptIn() && newsletter) {
      setOptIns(email).catch(() => {});
    }

    setIsProcessing(false);
  }

  return (
    <>
      <FormError message={error} />
      <form onSubmit={handleSubmit}>
        <div className={Styles.avatarHelper}>
          <strong>Choose your favorite avatar*</strong>
          <br />
          (You can change it from your profile at any time)
        </div>
        <div className="form-group">
          <AvatarSelect className={Styles.avatarSelect} changeAvatarHandler={(i) => setAvatar(i)} avatar={avatar} />
        </div>
        <div className={classNames('form-group')}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="birthday" className={classNames('form-label')}>
            Birthday*
            <BirthdayField
              birthday={birthday}
              setAge={(a) => {
                setAge(a);
              }}
              setBirthday={setBirthday}
            />
          </label>
        </div>
        {!!age && isCoppaAge(age) && (
          <div className={classNames('form-group')}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="inUnitedStates" className={classNames('form-label')}>
              Are you located in the US?*
            </label>
            <InUnitedStatesField onChange={(e) => setInUnitedStates(e.target.value)} id="inUnitedStates" />
          </div>
        )}
        {canRole() && isAdultAge(age) && (
          <div className={classNames('form-group')}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={classNames('form-label')}>
              What best describes you?*
              <RoleField role={roles[0] ? roles[0] : null} setRole={(role) => setRoles([role])} />
            </label>
          </div>
        )}
        {!!age && canEmail() && (
          <div className={classNames('form-group')}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control  */}
            <label className={classNames('form-label')}>
              Email*
              <EmailField value={email} required onChange={(e) => setEmail(e.target.value)} />
            </label>
          </div>
        )}
        {canSchool() && (
          <>
            <div className={classNames('form-group')}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className={classNames('form-label')}>
                State*
                <StateField onChange={(value) => setState(value)} value={state} />
              </label>
            </div>
            <div className={classNames('form-group')}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="schoolList" className={classNames('form-label')}>
                School Name*
              </label>
              <SchoolField onChange={(e) => setSchoolId(e)} state={state} />
              {roles.indexOf('parent') >= 0 && (
                <div className="form-helper">Parent / Caregiver, choose the school your child attends.</div>
              )}
            </div>
          </>
        )}
        {canGradeBand() && (
          <div className={classNames('form-group')}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={classNames('form-label')}>Grade*
              <GradeBandField setGradeBand={(gb) => setGradeBand(gb)} />
              {(roles.indexOf('teacher') >= 0 || roles.indexOf('parent') >= 0) && (
                <div className="form-helper">
                  Grownups, select the grade band that best describes your student(s).
                </div>
              )}
            </label>
          </div>
        )}
        {canClassCode() && (
          <div className={classNames('form-group')}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={classNames('form-label')}>
              Class Code <span style={{ fontWeight: 'normal' }}>(If you have one)</span>
            </label>
            <ClassCodeField
              defaultValue={getClassCode() ?? ''}
              onValidCode={(c) => {
                setCode(c);
              }}
              onInvalidCode={() => setCode('')}
              onBlankCode={() => setCode('')}
            />
          </div>
        )}
        {canOptIn() && (
          <div className={classNames('form-check', Styles.newsletter)}>
            <NewsletterField value={newsletter} onChange={(e) => setNewsletter(e.target.checked)} />
          </div>
        )}
        {!!age && (!isCoppaAge(age) || (isCoppaAge(age) && inUnitedStates)) && (
          <div className={classNames('form-actions')}>
            <FormProcessing isProcessing={isProcessing}>
              <Button size="md" type="submit" theme="blue" width="md">
                Finish Sign Up
              </Button>
              <Button size="md" theme="transparent" type="button" width="md" action={signOut}>
                Sign out
              </Button>
            </FormProcessing>
          </div>
        )}
      </form>
    </>
  );
}

SignUpRequirementsSsoUser.propTypes = {
  scrollToTop: PropTypes.func.isRequired,
};

export default SignUpRequirementsSsoUser;
